import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Paper, Typography, IconButton, Stack } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import TimerIcon from '@mui/icons-material/Timer';

const StopwatchWidget = () => {
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const startTimeRef = useRef(0);
  const intervalRef = useRef(null);
  const wakeLockRef = useRef(null);

  const requestWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) return;

    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
    } catch (err) {
      console.log('Wake Lock request failed:', err);
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
    }
  }, []);

  // Re-acquire wake lock when tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isRunning) {
        await requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isRunning, requestWakeLock]);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setTime(Date.now() - startTimeRef.current);
      }, 100);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      releaseWakeLock();
    };
  }, [releaseWakeLock]);

  const handleStart = async () => {
    setIsRunning(true);
    startTimeRef.current = Date.now() - time;
    await requestWakeLock();
  };

  const handleStop = () => {
    setIsRunning(false);
    releaseWakeLock();
  };

  const handleReset = () => {
    setIsRunning(false);
    setTime(0);
    releaseWakeLock();
  };

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const displayMinutes = minutes.toString().padStart(2, '0');
    const displaySeconds = seconds.toString().padStart(2, '0');
    return `${displayMinutes}:${displaySeconds}`;
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        py: 0.25,
        pl: 1.25,
        pr: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 'auto',
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        boxShadow: 1
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <TimerIcon fontSize="small" color={isRunning ? "primary" : "action"} />
        <Typography variant="body2" sx={{ fontWeight: 'bold', fontFamily: 'monospace', fontSize: '1rem', minWidth: '48px' }}>
          {formatTime(time)}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0}>
        {!isRunning ? (
          <IconButton size="small" onClick={handleStart} color="primary" sx={{ padding: 0.5 }}>
            <PlayArrowIcon fontSize="small" />
          </IconButton>
        ) : (
          <IconButton size="small" onClick={handleStop} color="warning" sx={{ padding: 0.5 }}>
            <PauseIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton size="small" onClick={handleReset} disabled={time === 0 && !isRunning} sx={{ padding: 0.5 }}>
          <ReplayIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
};

export default StopwatchWidget;